import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { Bootstrap } from '@/lib/bootstrap';
import type { BetBundle, RuleDefinition } from '@/types';
import { fetchClients } from '@/features/bet-inspection/lib/api';
import { RuleEditor } from './components/RuleEditor';
import { RuleList } from './components/RuleList';
import { RulePreview } from './components/RulePreview';
import { STATIC_BUNDLE, type SuggestModel } from './lib/autocomplete';
import { deleteRule, fetchRules, saveRule } from './lib/api';
import type { PreviewResult } from './lib/preview';

const blankRule = (): RuleDefinition => ({
  id: '',
  name: '',
  severity: 'info',
  expression: '',
  message: '',
  lists: {},
});

/**
 * Rule Builder — author, test, and store hint rules.
 *
 * Three panes: the saved rules (left), the editor (centre), and a LIVE preview
 * against a real bet (right). The preview evaluates the draft with the same
 * engine the server uses, so a rule is proven before it's saved. Rules persist
 * to the welcome DB via the /rules API.
 */
export function RuleBuilder({ boot }: { boot: Bootstrap }) {
  const qc = useQueryClient();
  const api = boot.api!;
  const token = boot.token;

  const rulesQuery = useQuery({
    queryKey: ['rules', api],
    queryFn: () => fetchRules({ api, token }),
    enabled: Boolean(api),
    retry: 0,
  });

  const clients = useQuery({
    queryKey: ['clients', api],
    queryFn: ({ signal }) => fetchClients({ api, token, signal }),
    enabled: Boolean(api),
    staleTime: 5 * 60_000,
  });

  const [draft, setDraft] = useState<RuleDefinition>(blankRule);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [sampleBundle, setSampleBundle] = useState<BetBundle | null>(null);

  // Autocomplete navigates a REAL bundle once a sample bet is loaded (so it
  // offers this game's actual columns); before that, a shape-only stand-in.
  const model = useMemo<SuggestModel>(
    () => ({
      bundle: sampleBundle ?? STATIC_BUNDLE,
      lists: draft.lists ?? {},
      matched: preview?.matched,
    }),
    [sampleBundle, draft.lists, preview],
  );

  const savedRules = rulesQuery.data ?? [];
  const original = useMemo(
    () => savedRules.find((r) => r.id === selectedId) ?? null,
    [savedRules, selectedId],
  );
  const isExisting = Boolean(original);
  // Compare canonically (fixed field order) — key order must not read as "dirty".
  const dirty = useMemo(
    () => (original ? canon(original) !== canon(draft) : hasContent(draft)),
    [original, draft],
  );

  const select = (rule: RuleDefinition) => {
    setSelectedId(rule.id);
    setDraft({ ...blankRule(), ...rule, lists: rule.lists ?? {} });
    setPreview(null);
  };
  const newRule = () => {
    setSelectedId(null);
    setDraft(blankRule());
    setPreview(null);
  };

  const save = useMutation({
    mutationFn: () => saveRule({ api, token, rule: normalize(draft) }),
    onSuccess: (rule) => {
      setSelectedId(rule.id);
      qc.invalidateQueries({ queryKey: ['rules', api] });
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteRule({ api, token, id: draft.id }),
    onSuccess: () => {
      newRule();
      qc.invalidateQueries({ queryKey: ['rules', api] });
    },
  });

  return (
    <div className="grid min-h-0 flex-1 grid-cols-[240px_minmax(0,1fr)_380px]">
      <RuleList
        rules={savedRules}
        loading={rulesQuery.isLoading}
        error={rulesQuery.isError ? errText(rulesQuery.error) : undefined}
        selectedId={selectedId}
        dirtyId={dirty ? selectedId : null}
        onSelect={select}
        onNew={newRule}
      />

      <RuleEditor
        draft={draft}
        onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        onSave={() => save.mutate()}
        onDelete={() => {
          if (confirm(`Delete rule "${draft.name || draft.id}"?`)) remove.mutate();
        }}
        saving={save.isPending}
        deleting={remove.isPending}
        dirty={dirty}
        isExisting={isExisting}
        saveError={save.isError ? errText(save.error) : undefined}
        expressionError={preview?.expressionError}
        templateError={preview?.templateError}
        model={model}
      />

      <RulePreview
        draft={draft}
        api={api}
        token={token}
        clients={clients.data ?? []}
        clientsLoading={clients.isLoading}
        onResult={setPreview}
        onBundle={setSampleBundle}
      />
    </div>
  );
}

/** Drop an empty `lists` so a rule with no lists compares equal to its stored form. */
function normalize(rule: RuleDefinition): RuleDefinition {
  const lists = rule.lists && Object.keys(rule.lists).length > 0 ? rule.lists : undefined;
  const out = { ...rule };
  if (lists) out.lists = lists;
  else delete out.lists;
  return out;
}

/**
 * Canonical string for equality — a fixed field order and sorted list keys, so a
 * rule loaded from the API compares equal to the same rule in the editor
 * regardless of how the two happened to order their JSON keys.
 */
function canon(rule: RuleDefinition): string {
  const lists = rule.lists ?? {};
  const sortedLists = Object.keys(lists)
    .sort()
    .reduce<Record<string, unknown[]>>((acc, k) => ((acc[k] = lists[k]), acc), {});
  return JSON.stringify({
    id: rule.id,
    name: rule.name,
    severity: rule.severity ?? 'info',
    expression: rule.expression,
    message: rule.message,
    lists: Object.keys(sortedLists).length ? sortedLists : null,
  });
}

function hasContent(rule: RuleDefinition): boolean {
  return Boolean(rule.id || rule.name || rule.expression || rule.message);
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
